import { AlertTriangle, Boxes, CalendarClock, Check, Loader2, Users, Wrench, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { InventoryProposal, InventoryRequirement, StaffingLine } from '@/api/processStepsApi'
// The same person-hours arithmetic the builder's summary bar uses, so the
// preview and the configuration screen can never quote different numbers.
import { hrs, personHoursFor, toStaffingDraft, totalPersonHours } from '@/lib/processStaffing'

/**
 * The automation, offered at the moment the operator names a time.
 *
 * Setting an expected start is the only input auto-select needs, so this opens
 * off that field rather than waiting behind the Start dialog — the point of the
 * feature is that the operator does not have to go and assemble the run by
 * hand. It is a CONFIRMATION, not a fait accompli: it says what it picked and
 * what that will cost before anything is held.
 *
 * **It deliberately does not lock inventory.** Holding stock is part of the
 * signed Start transition (21 CFR Part 11) and taking it here would be a
 * regulated write with no signature behind it. What this does do is reserve the
 * equipment — an unsigned hold the manual Reserve button has always been allowed
 * to place — and carry the lot proposal into the Start dialog, where the
 * operator signs for it. The copy below says exactly that rather than implying
 * the stock is already theirs.
 */
export function AutoSelectConfirmDialog({
  open,
  stepName,
  expectedStart,
  equipmentWindowStart,
  equipment,
  proposals,
  inventory,
  proposalsFailed,
  staffing,
  loading,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean
  stepName: string
  /** Formatted expected start — the time the operator just entered. */
  expectedStart: string
  /**
   * Formatted start of the EARLIEST equipment hold, or null when there is no
   * equipment. Each requirement adds its own prep to `expectedStart`, so the
   * units may be wanted at different times and this is the first of them.
   */
  equipmentWindowStart: string | null
  /** One entry per configured equipment requirement, picked or not. */
  equipment: {
    requirementId: string
    typeName: string
    unitId: string | null
    unitLabel: string | null
    /** Why there is no unit, or how this one was chosen. */
    note: string | null
  }[]
  /** The FEFO proposal, or null while it is still being fetched. */
  proposals: InventoryProposal[] | null
  /**
   * Every material the step configures — the same contract `equipment` has.
   *
   * The Materials section is driven by THIS, not by `proposals`. Keying it off
   * the proposal conflated three different states into one blank screen: no
   * materials configured, the proposal still failing, and a proposal that came
   * back empty. An operator was shown equipment alone and given no reason to
   * think anything was missing.
   */
  inventory: InventoryRequirement[]
  /** The proposal request failed, as opposed to returning nothing. */
  proposalsFailed: boolean
  /**
   * Every staffing line the step configures.
   *
   * Reported, never selected — see the section below. It is here because the
   * dialog used to list only what it picks, which left an operator reading a
   * step's whole cost from a screen that silently omitted a third of it.
   */
  staffing: StaffingLine[]
  loading: boolean
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const short = (proposals ?? []).filter(p => p.isShort)
  const covered = (proposals ?? []).filter(p => !p.isShort)
  const bookable = equipment.filter(e => !!e.unitId)
  const staffingDrafts = staffing.map(toStaffingDraft)
  // The longest prep across roles, which is what delays the equipment: prep
  // runs in parallel across roles, so the max is the wait, not the sum. Same
  // rule as `prepHoursFor` on the server.
  const longestPrepHrs = staffingDrafts.reduce((max, d) => Math.max(max, hrs(d.prepHrs)), 0)
  // Nothing to SELECT is not the same as nothing to report: a step can have its
  // materials and equipment already resolved and still cost people time.
  //
  // Counted from what the process CONFIGURES, never from what the proposal
  // returned — a step with six materials whose proposal failed still has six
  // materials to select, and saying otherwise is how the failure went unseen.
  const nothingToSelect = inventory.length === 0 && equipment.length === 0
  // Configured materials the proposal did not answer for. Normally zero: the
  // server returns one row per requirement, shortfalls included.
  const proposedIds = new Set((proposals ?? []).map(p => p.requirementId))
  const unresolved = inventory.filter(r => !proposedIds.has(r.id))
  const nothingToDo = !loading && nothingToSelect && staffing.length === 0

  return (
    <Dialog open={open} onOpenChange={o => !o && onCancel()}>
      <DialogContent className='max-w-lg gap-0 overflow-hidden p-0'>
        {/* A coloured header band rather than a bare title: this dialog offers
            to act on the operator's behalf, and it should look like an offer. */}
        <DialogHeader className='space-y-0 border-b border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 px-6 py-5'>
          <div className='flex items-start gap-3'>
            <span className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600/10 text-blue-700'>
              <Zap className='h-4 w-4' />
            </span>
            <div className='min-w-0'>
              <DialogTitle className='text-base font-semibold text-gray-900'>
                Auto-select for {stepName}?
              </DialogTitle>
              <p className='mt-1 flex items-center gap-1.5 text-xs text-gray-600'>
                <CalendarClock className='h-3.5 w-3.5 shrink-0 text-gray-400' />
                Starting <span className='font-medium text-gray-800'>{expectedStart}</span>
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className='max-h-[22rem] space-y-4 overflow-y-auto px-6 py-5'>
          {loading ? (
            <p className='flex items-center gap-2 py-6 text-sm text-gray-500'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Working out what this run needs…
            </p>
          ) : nothingToDo ? (
            <p className='py-6 text-sm text-gray-500'>
              This step configures no materials, equipment or staffing, so there is nothing to
              select.
            </p>
          ) : (
            <>
              {/* A step whose only configuration is people. Said plainly, so
                  the staffing below does not read as something being booked. */}
              {nothingToSelect && (
                <p className='text-sm text-gray-500'>
                  There are no materials or equipment to select on this step — this is what it
                  needs.
                </p>
              )}

              {/* Materials — what it picked, and why that lot.
                  Gated on what the step CONFIGURES, matching Equipment below,
                  so a material never goes unmentioned just because no lot
                  could be proposed for it. */}
              {inventory.length > 0 && (
                <section className='space-y-2'>
                  <h3 className='flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                    <Boxes className='h-3.5 w-3.5' />
                    Materials · picked first-to-expire
                  </h3>
                  {/* The proposal is what fills this section in; the section
                      itself exists either way. Said once, above the list, so a
                      run is never confirmed on a materials picture the server
                      failed to produce. */}
                  {proposalsFailed && (
                    <p className='flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
                      <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                      <span>
                        Lots could not be worked out for{' '}
                        {inventory.length === 1
                          ? 'this material'
                          : `these ${inventory.length} materials`}
                        . Confirming still books the equipment below — allocate the lots on the
                        step itself.
                      </span>
                    </p>
                  )}
                  <ul className='divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200'>
                    {(proposals ?? []).map(p => (
                      <li key={p.requirementId} className='flex items-start gap-3 px-3 py-2.5'>
                        <span
                          className={cn(
                            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                            p.isShort ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700',
                          )}
                        >
                          {p.isShort ? (
                            <AlertTriangle className='h-3 w-3' />
                          ) : (
                            <Check className='h-3 w-3' />
                          )}
                        </span>
                        <div className='min-w-0 flex-1'>
                          <p className='truncate text-sm font-medium text-gray-900'>{p.itemLabel}</p>
                          {p.lots.length > 0 ? (
                            <p className='mt-0.5 text-xs text-gray-500'>
                              {p.lots.map(l => `${l.lot} × ${l.qty}`).join(' · ')}
                            </p>
                          ) : (
                            <p className='mt-0.5 text-xs text-amber-700'>No free stock on the shelf</p>
                          )}
                          {p.isShort && p.lots.length > 0 && (
                            <p className='mt-0.5 text-xs text-amber-700'>
                              {p.shortfall} short of {p.qtyRequired}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                    {/* Configured, but the proposal said nothing about it —
                        the request failed, or it answered for fewer rows than
                        the step defines. Listed rather than dropped: the
                        operator is about to commit to a start time, and the
                        count of materials they see has to be the count the
                        step actually needs. */}
                    {unresolved.map(r => (
                      <li key={r.id} className='flex items-start gap-3 px-3 py-2.5'>
                        <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500'>
                          <Boxes className='h-3 w-3' />
                        </span>
                        <div className='min-w-0 flex-1'>
                          <p className='truncate text-sm font-medium text-gray-900'>
                            {r.itemLabel?.trim() || r.consumableId}
                          </p>
                          <p className='mt-0.5 text-xs text-gray-500'>
                            Needs {r.qtyRequired}
                            {r.uom ? ` ${r.uom}` : ''} · no lot proposed
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Equipment — the only thing this dialog actually holds. */}
              {equipment.length > 0 && (
                <section className='space-y-2'>
                  <h3 className='flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                    <Wrench className='h-3.5 w-3.5' />
                    Equipment · first free unit
                  </h3>
                  <ul className='divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200'>
                    {equipment.map(e => (
                      <li key={e.requirementId} className='flex items-start gap-3 px-3 py-2.5'>
                        <span
                          className={cn(
                            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                            e.unitId
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700',
                          )}
                        >
                          {e.unitId ? <Check className='h-3 w-3' /> : <AlertTriangle className='h-3 w-3' />}
                        </span>
                        <div className='min-w-0 flex-1'>
                          <p className='truncate text-sm font-medium text-gray-900'>{e.typeName}</p>
                          <p
                            className={cn(
                              'mt-0.5 truncate text-xs',
                              e.unitId ? 'text-gray-500' : 'text-amber-700',
                            )}
                          >
                            {e.unitLabel ?? e.note}
                            {e.unitId && e.note && (
                              <span className='text-gray-400'> · {e.note.toLowerCase()}</span>
                            )}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {bookable.length > 0 && (
                    <p className='text-xs text-gray-500'>
                      Held
                      {equipmentWindowStart && (
                        <>
                          {' '}
                          from <span className='font-medium text-gray-700'>{equipmentWindowStart}</span>
                        </>
                      )}
                      {equipmentWindowStart && equipmentWindowStart !== expectedStart && (
                        <> — each unit once its own prep is done</>
                      )}
                      .
                    </p>
                  )}
                </section>
              )}

              {/* Staffing — REPORTED, not selected.
                  There is nothing to allocate: no people are booked, no roster
                  is consulted, and confirming does not put anyone's name
                  against this step. It is here because it is a third of what
                  the step costs, and a preview that showed only the machines
                  and the lots let an operator commit to a time without seeing
                  the person-hours behind it. The heading says "needs" for that
                  reason — every other section here says what was picked. */}
              {staffing.length > 0 && (
                <section className='space-y-2'>
                  <h3 className='flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                    <Users className='h-3.5 w-3.5' />
                    Staffing · needs {totalPersonHours(staffingDrafts)} person-hours
                  </h3>
                  <ul className='divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200'>
                    {staffing.map((line, i) => {
                      const draft = staffingDrafts[i]
                      const people = draft.numResources
                      const prep = hrs(draft.prepHrs)
                      return (
                        <li key={line.id} className='flex items-start gap-3 px-3 py-2.5'>
                          <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500'>
                            <Users className='h-3 w-3' />
                          </span>
                          <div className='min-w-0 flex-1'>
                            <p className='truncate text-sm font-medium text-gray-900'>
                              {/* A line can be saved before its role is named. */}
                              {line.roleLabel?.trim() || 'Unnamed role'}
                            </p>
                            <p className='mt-0.5 truncate text-xs text-gray-500'>
                              {people} {Number(people) === 1 ? 'person' : 'people'}
                              {prep > 0 && <> · {prep} h prep</>}
                            </p>
                          </div>
                          <span className='shrink-0 text-xs tabular-nums text-gray-500'>
                            {personHoursFor(draft)} person-h
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                  {/* Explains the "from …" time the equipment section prints,
                      which otherwise looks like an unexplained delay. */}
                  {longestPrepHrs > 0 && bookable.length > 0 && (
                    <p className='text-xs text-gray-500'>
                      The longest prep is why the units are held after the step&apos;s own start —
                      prep runs in parallel across roles, so the wait is the longest one, not their
                      sum.
                    </p>
                  )}
                </section>
              )}

              {/* The honest bit: what confirming does NOT do. */}
              <p className='rounded-lg bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-600'>
                {covered.length > 0 || short.length > 0 ? (
                  <>
                    These lots are <span className='font-medium'>proposed</span>, not held — you can
                    change any of them, and the stock is locked when you sign Start.
                  </>
                ) : (
                  'Nothing is locked until you sign Start.'
                )}
              </p>

              {short.length > 0 && (
                <p className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900'>
                  <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  <span>
                    {short.length} material{short.length === 1 ? '' : 's'} cannot be fully covered by
                    free stock. You can still proceed and sort the shortfall out before signing.
                  </span>
                </p>
              )}
            </>
          )}

          {error && (
            <p role='alert' className='rounded-lg bg-red-50 px-3 py-2.5 text-xs font-medium text-red-700'>
              {error}
            </p>
          )}
        </div>

        <DialogFooter className='gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-4'>
          <Button variant='ghost' onClick={onCancel} disabled={busy}>
            Not now
          </Button>
          <Button onClick={onConfirm} disabled={busy || loading || nothingToDo}>
            {busy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {bookable.length > 0 ? 'Confirm & reserve' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
